import type { SlotType } from './imageUtils';

/**
 * Maps short ImageKey names to SlotType field names.
 * Used to convert from imageTypeConfig.ts keys to imageUtils.ts keys.
 */
export const IMAGE_KEY_TO_SLOT: Record<string, SlotType> = {
  vertical: 'profile_vertical_image',
  square: 'profile_image',
  horizontal: 'profile_horizontal_image',
  circle: 'profile_circle_image',
  default_cover: 'default_cover_art',
  radio_cover: 'default_cover_art',
  tv_cover: 'default_cover_art',
};

/** Image-related tools that should show inline image previews */
export const IMAGE_TOOLS = new Set(['generate_image', 'update_profile_image']);

/** Regex to match markdown image syntax: ![alt](url) */
const MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;

/**
 * Parses user prompt text to determine the target slot based on aspect ratio mentions.
 * Checks for explicit aspect ratios (16:9, 9:16, 1:1) and keywords (horizontal, vertical, square).
 */
export function parseSlotFromPrompt(prompt: string): SlotType | null {
  const normalized = prompt.toLowerCase();

  if (normalized.includes('16:9') || normalized.includes('16x9')) {
    return 'profile_horizontal_image';
  }
  if (normalized.includes('9:16') || normalized.includes('9x16')) {
    return 'profile_vertical_image';
  }
  if (normalized.includes('1:1') || normalized.includes('1x1')) {
    return 'profile_image';
  }

  if (normalized.includes('horizontal_image') || normalized.includes('horizontal profile')) {
    return 'profile_horizontal_image';
  }
  if (normalized.includes('horizontal')) {
    return 'profile_horizontal_image';
  }
  if (normalized.includes('vertical_image') || normalized.includes('vertical profile')) {
    return 'profile_vertical_image';
  }
  if (normalized.includes('vertical')) {
    return 'profile_vertical_image';
  }
  if (normalized.includes('square')) {
    return 'profile_image';
  }

  return null;
}

export function formatToolName(tool: string): string {
  return tool.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function extractImageUrl(result: unknown): string | null {
  if (!result) return null;
  if (typeof result === 'string') return result;
  if (typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    if (typeof obj.imageUrl === 'string') return obj.imageUrl;
    if (typeof obj.url === 'string') return obj.url;
    if (typeof obj.data === 'object' && obj.data) {
      const data = obj.data as Record<string, unknown>;
      if (typeof data.imageUrl === 'string') return data.imageUrl;
      if (typeof data.url === 'string') return data.url;
    }
  }
  return null;
}

export function parseMarkdownImages(content: string): { text: string; images: { alt: string; url: string }[] } {
  const images: { alt: string; url: string }[] = [];
  const text = content.replace(MARKDOWN_IMAGE_REGEX, (_match, alt, url) => {
    images.push({ alt, url });
    return '';
  });
  return { text: text.trim(), images };
}
